import React from "react";
import _ from "lodash";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import { useAppInsightsContext } from "@microsoft/applicationinsights-react-js";

import NotificationBar from "$components/notification-bar";
import DateInput from "$components/form-inputs/date-picker";
import Select from "$components/form-inputs/select";
import { Input } from "$components/form-inputs/inputs";
import { extractValue } from "$components/form-inputs/libs/helpers";

import { submitOpportunityToRebel } from "$stores/rebel-group";

import "./rebel-group.scss";

// Placeholder vocabulary until Rebel Group provides their deal-stage list (issue #723)
const STAGE_OPTIONS = [
    { value: "new", text: "New" },
    { value: "qualified", text: "Qualified" },
    { value: "proposal", text: "Proposal" },
];

const formatDate = (date) => {
    if (!date) return "";
    const d = new Date(date);
    const month = `${d.getMonth() + 1}`.padStart(2, "0");
    const day = `${d.getDate()}`.padStart(2, "0");
    return `${d.getFullYear()}-${month}-${day}`;
};

const RebelGroupOpportunityForm = ({ projectKey, currentProject, afterSubmit }) => {
    const appInsights = useAppInsightsContext();

    const initialValues = {
        name: currentProject?.name || "",
        stage: STAGE_OPTIONS[0].value,
        expectedCloseDate: "",
    };

    const handleSubmit = async (values) => {
        // api.put rejects on most failures (after showing its own toast when the server
        // provides a message) and resolves to a falsy value on others (e.g. 404) — catching
        // here ensures a failure is always visible AND that Formik's isSubmitting always
        // resets, since an uncaught rejection here would leave the Submit button stuck
        // disabled with no way to retry (issue #729).
        try {
            const response = await submitOpportunityToRebel(projectKey, {
                name: values.name,
                stage: extractValue(values.stage),
                expectedCloseDate: formatDate(values.expectedCloseDate),
            });
            if (response) {
                toast("Sent to CRM Successfully");
                appInsights.trackEvent(
                    { name: "RebelGroup" },
                    {
                        label: "Submitted",
                        data: projectKey,
                        page: document.title.replace(" - Orgaimi", ""),
                    }
                );
                if (afterSubmit) afterSubmit();
            } else {
                toast("Could not send to CRM — please try again");
            }
        } catch (error) {
            toast("Could not send to CRM — please try again");
        }
    };

    return (
        <div className="rebel-group-form">
            <h1>
                <em>Send to</em> CRM
            </h1>

            <Formik
                initialValues={initialValues}
                validate={(values) => {
                    const errors = {};
                    if (!values.name) errors.name = "Name is required";
                    if (!extractValue(values.stage)) errors.stage = "Stage is required";
                    if (!values.expectedCloseDate) errors.expectedCloseDate = "Expected Close Date is required";
                    return errors;
                }}
                onSubmit={async (values, { isSubmitting, setSubmitting }) => {
                    if (isSubmitting) {
                        return;
                    }
                    await handleSubmit(values);
                    setSubmitting(false);
                }}
            >
                {({ errors, touched, submitCount, isSubmitting, handleChange, setFieldValue, values }) => (
                    <Form>
                        {!_.isEmpty(errors) && submitCount > 0 && (
                            <NotificationBar type="error">
                                <ul>
                                    {Object.keys(errors).map((k) => {
                                        return <li key={`error_${k}`}>{errors[k]}</li>;
                                    })}
                                </ul>
                            </NotificationBar>
                        )}
                        <div className="fields">
                            <div className="form-group">
                                <Field
                                    type="text"
                                    id="name"
                                    name="name"
                                    label="*Name"
                                    component={Input}
                                    onChange={handleChange}
                                    updateOnChange={(name, value) => {
                                        setFieldValue(name, extractValue(value));
                                    }}
                                    invalid={!!errors.name && (touched.name || submitCount > 0)}
                                    defaultValue={values.name}
                                />
                            </div>
                            <div className="form-group">
                                <Field
                                    id="stage"
                                    name="stage"
                                    label="*Stage"
                                    options={STAGE_OPTIONS}
                                    component={Select}
                                    updateOnChange={(name, value) => {
                                        setFieldValue(name, extractValue(value));
                                    }}
                                    invalid={!!errors.stage && (touched.stage || submitCount > 0)}
                                    defaultValue={values.stage}
                                />
                            </div>
                            <div className="form-group">
                                <Field
                                    type="date"
                                    id="expectedCloseDate"
                                    name="expectedCloseDate"
                                    label="*Expected Close Date"
                                    component={DateInput}
                                    updateOnChange={(name, value) => {
                                        setFieldValue(name, value);
                                    }}
                                    minDate={new Date()}
                                    placeholderText="Select a date"
                                    showIcon={true}
                                    toggleCalendarOnIconClick={true}
                                    invalid={!!errors.expectedCloseDate && (touched.expectedCloseDate || submitCount > 0)}
                                    defaultValue={values.expectedCloseDate}
                                />
                            </div>
                        </div>
                        <div className="button-group -right">
                            <button type="submit" className="button -primary" disabled={isSubmitting}>
                                Submit
                            </button>
                        </div>
                    </Form>
                )}
            </Formik>
        </div>
    );
};

export default RebelGroupOpportunityForm;
